import React from 'react';
import { GetHighlightsMapping } from '@greystar/lib';
import { PropertyAmenitiesProps, ListItemProps } from '../../../component-props';
import { Header, IconTextList, LayoutContainer, List } from '@greystar/designsystem';

const hasData = (props: PropertyAmenitiesProps): boolean => {
  return (
    !!props.highlights ||
    (Array.isArray(props.features) && props.features.length > 0) ||
    (Array.isArray(props.amenities) && props.amenities.length > 0)
  );
};

/**
 * Component to display property amenities.
 *
 * @param {PropertyAmenitiesProps} props - The properties for the component.
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * This component renders a section containing the title, highlights, features, and amenities of a property.
 * If there is no data available (as determined by the `hasData` function), it returns an empty fragment.
 *
 * @example
 * ```tsx
 * <PropertyAmenities
 *   title="Amenities"
 *   highlights={{ airCon: true }}
 *   highlightsTitle="Highlights"
 *   features={["Pool", "Gym"]}
 *   featuresTitle="Features"
 *   amenities={["WiFi", "Parking"]}
 *   amenitiesTitle="Amenities"
 * />
 * ```
 *
 * @component
 * @public
 */
export const PropertyAmenities = (props: PropertyAmenitiesProps): JSX.Element => {
  if (!hasData(props)) {
    return <></>;
  }
  const HIGHLIGHTS_MAPPING = GetHighlightsMapping();

  const highlightItems = props.highlights
    ? Object.entries(props.highlights)
        .filter(([key, value]) => key != '' && value === true) // Check if the value is true
        .map(([key]) => ({
          iconName: HIGHLIGHTS_MAPPING[key as keyof typeof HIGHLIGHTS_MAPPING].icon,
          text: HIGHLIGHTS_MAPPING[key as keyof typeof HIGHLIGHTS_MAPPING].text,
        }))
    : [];

  const listItems: ListItemProps[] = [];

  if (props.features && props.features.length > 0) {
    listItems.push({
      heading: props.featuresTitle,
      descriptionItems: props.features,
    });
  }

  if (props.amenities && props.amenities.length > 0) {
    listItems.push({
      heading: props.amenitiesTitle,
      descriptionItems: props.amenities,
    });
  }

  return (
    <>
      <Header component="h3" className="py-4 lg:pt-[20px] md:pb-[26px]">
        {props.title}
      </Header>
      {props.highlights && (
        <LayoutContainer
          maxWidth="xlg"
          margin="auto"
          flexDirection="column"
          paddingTop="xl"
          paddingBottom="sm"
        >
          <Header component="h4" className="mb-4">
            {props.highlightsTitle}
          </Header>
          <IconTextList iconTextPairs={highlightItems} size={props.size ?? 'md'} />
        </LayoutContainer>
      )}
      {listItems.length > 0 && (
        <LayoutContainer
          margin="auto"
          flexDirection="column"
          paddingTop="xl"
          maxWidth="xlg"
          className="[&_.grid--column-sm--span--4:nth-child(2)_.list-item]:lg:pl-[32px]"
        >
          <List listItems={listItems} lgColumnSpan="6" />
        </LayoutContainer>
      )}
    </>
  );
};
